import React, { useState, useRef, useEffect, useCallback } from "react";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { ChevronDown, ChevronRight, GitBranch, Loader2 } from "lucide-react";
import EmailCreativeFingerprintPanel from "./EmailCreativeFingerprintPanel";
import EmailCreativeVersionBroadcastTable from "./EmailCreativeVersionBroadcastTable";
import { getCreativeVersionDetail, getBroadcasts, getBroadcastDetail, extractApiErrorMessage } from "../../lib/emailMarketingApi";

/**
 * EmailCreativeVersionTable — DASH-EMAIL-011b, cut over in DASH-EMAIL-012A
 * (Frozen Decision 6) from a synchronous inline mock lookup to a real,
 * asynchronous, row-scoped fetch. This is a required architectural change,
 * not a data-source swap: the mock's `getCreativeVersionDetail` was a pure,
 * synchronous function call made directly inside render; the real
 * equivalent is a network request, so expanding a row now triggers an
 * explicit fetch with its own loading/error/not-found state, scoped to
 * that row only — the rest of the page (other rows, the Creative identity
 * card above) never blocks on it.
 *
 * Each Version row is also thinner than before: the real
 * `CreativeVersionSummary` list item is only `{version, created_at}` — no
 * per-version broadcastCount/recipientDeliveries/metrics exist at list
 * grain (that richer detail only exists on the full per-version detail
 * response, fetched on expansion). `created_at` is the catalog record's own
 * creation time, not an execution/use timestamp (see
 * email_marketing_catalog_api.py's own field-level comment) — labeled
 * "Catalog record created" rather than "First used" to avoid implying a
 * fact the real catalog does not capture.
 *
 * Broadcast usage (DASH-EMAIL-012A Frozen Decision 7): the real Version
 * detail response has no embedded `broadcasts` field
 * (CreativeVersionDetailResponse only carries `broadcast_count: int`) — the
 * per-Broadcast rows come from GET /broadcasts?creative_id=X, fetched ONCE
 * per Creative (cached across every Version's expansion, not refetched per
 * row) and filtered client-side by `creative_version === version`. Each
 * matching Broadcast is then fetched at full detail grain
 * (GET /broadcasts/{id}) — attribution and Recipient Snapshot only exist on
 * that per-Broadcast detail response, not the list item — bounded to just
 * the (typically 1-3) Broadcasts pinning THIS specific Version, never every
 * Broadcast in the Creative's history and never prefetched for a Version
 * that hasn't been expanded.
 */
export default function EmailCreativeVersionTable({ creativeId, versions, angleLookup, campaignLookup, initialExpandedVersion, onNavigateToAsset }) {
  const [expandedVersion, setExpandedVersion] = useState(initialExpandedVersion || null);
  const [versionDetails, setVersionDetails] = useState({});
  const [creativeBroadcasts, setCreativeBroadcasts] = useState({ status: "idle", items: [] });
  const [versionBroadcastDetails, setVersionBroadcastDetails] = useState({});

  const loadVersionDetail = useCallback(
    async (version) => {
      setVersionDetails((prev) => ({ ...prev, [version]: { status: "loading" } }));
      try {
        const data = await getCreativeVersionDetail(creativeId, version);
        setVersionDetails((prev) => ({ ...prev, [version]: data ? { status: "ready", data } : { status: "not_found" } }));
      } catch (err) {
        setVersionDetails((prev) => ({ ...prev, [version]: { status: "error", message: extractApiErrorMessage(err, "Failed to load Version detail") } }));
      }
    },
    [creativeId]
  );

  // Fetches the Creative-wide Broadcast list once (cached in state across
  // every Version's expansion), then resolves full per-Broadcast detail
  // (attribution + Recipient Snapshot) only for the Broadcasts pinning this
  // one Version. If the shared list is already mid-flight from a
  // just-expanded sibling row, this expansion's own detail resolution is
  // skipped rather than raced against it — an accepted simplification given
  // the real corpus's small Broadcast counts and that this is a manual,
  // click-driven interaction, not a hot path.
  const loadVersionBroadcasts = useCallback(
    async (version) => {
      let list = creativeBroadcasts.items;
      if (creativeBroadcasts.status === "idle") {
        setCreativeBroadcasts({ status: "loading", items: [] });
        try {
          const result = await getBroadcasts({ creativeId, limit: 100 });
          list = result.items || [];
          setCreativeBroadcasts({ status: "ready", items: list });
        } catch {
          setCreativeBroadcasts({ status: "error", items: [] });
          setVersionBroadcastDetails((prev) => ({ ...prev, [version]: { status: "error", items: [] } }));
          return;
        }
      } else if (creativeBroadcasts.status === "loading") {
        return;
      }

      const matching = list.filter((b) => b.creative_version === version);
      setVersionBroadcastDetails((prev) => ({ ...prev, [version]: { status: "loading", items: [] } }));
      try {
        const details = await Promise.all(matching.map((b) => getBroadcastDetail(b.broadcast_id)));
        setVersionBroadcastDetails((prev) => ({ ...prev, [version]: { status: "ready", items: details.filter(Boolean) } }));
      } catch {
        setVersionBroadcastDetails((prev) => ({ ...prev, [version]: { status: "error", items: [] } }));
      }
    },
    [creativeId, creativeBroadcasts.status, creativeBroadcasts.items]
  );

  const handleToggle = (version) => {
    if (expandedVersion === version) {
      setExpandedVersion(null);
      return;
    }
    setExpandedVersion(version);
    if (!versionDetails[version] || versionDetails[version].status === "error") {
      loadVersionDetail(version);
    }
    if (!versionBroadcastDetails[version] || versionBroadcastDetails[version].status === "error") {
      loadVersionBroadcasts(version);
    }
  };

  // Deep-link support (Asset detail -> exact Creative Version, DASH-EMAIL-011
  // §10 — still in scope; only the Performance-originated cross-navigation
  // was removed in DASH-EMAIL-012A, see EmailCreativeView.jsx). The parent
  // (EmailCreativeDetail) keys this component on `initialExpandedVersion`,
  // so a fresh navigation target always produces a fresh mount — a
  // mount-only effect is therefore the correct, complete trigger, matching
  // this component's pre-existing remount-based reset pattern.
  const didInitialLoad = useRef(false);
  useEffect(() => {
    if (initialExpandedVersion && !didInitialLoad.current) {
      didInitialLoad.current = true;
      loadVersionDetail(initialExpandedVersion);
      loadVersionBroadcasts(initialExpandedVersion);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = versions || [];

  if (rows.length === 0) {
    return (
      <Card className="glass border-white/10" data-testid="email-creative-version-table-empty">
        <CardContent className="p-8 text-center text-slate-400">
          <p className="text-lg">No Versions for this selection</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass border-white/10" data-testid="email-creative-version-table">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg text-slate-100">
          <GitBranch className="h-5 w-5 text-indigo-400" />
          Version History
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="w-8 text-slate-400" />
              <TableHead className="text-slate-400">Version</TableHead>
              <TableHead className="text-slate-400">Catalog Record Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              const isExpanded = expandedVersion === row.version;
              const versionState = versionDetails[row.version];
              const broadcastState = versionBroadcastDetails[row.version];
              const rowId = `${creativeId}-${row.version}`;
              return (
                <React.Fragment key={row.version}>
                  <TableRow
                    className="cursor-pointer border-white/10 hover:bg-white/5"
                    data-testid={`email-creative-version-row-${rowId}`}
                    onClick={() => handleToggle(row.version)}
                  >
                    <TableCell>
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-slate-400" data-testid={`email-creative-version-chevron-down-${rowId}`} />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-slate-400" data-testid={`email-creative-version-chevron-right-${rowId}`} />
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-medium text-slate-100">
                        {creativeId} <span className="text-slate-400">v{row.version}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-slate-300">{row.created_at || "—"}</TableCell>
                  </TableRow>
                  {isExpanded && (
                    <TableRow className="border-white/10 hover:bg-transparent" data-testid={`email-creative-version-detail-${rowId}`}>
                      <TableCell />
                      <TableCell colSpan={2}>
                        <div className="space-y-4 py-2">
                          {(!versionState || versionState.status === "loading") && (
                            <p className="flex items-center gap-1.5 text-xs text-slate-400" data-testid={`email-creative-version-loading-${rowId}`}>
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              Loading Version detail…
                            </p>
                          )}
                          {versionState?.status === "not_found" && (
                            <p className="text-xs text-red-300" data-testid={`email-creative-version-not-found-${rowId}`}>
                              This Version could not be found.
                            </p>
                          )}
                          {versionState?.status === "error" && (
                            <p className="text-xs text-red-300" data-testid={`email-creative-version-error-${rowId}`}>
                              Failed to load Version detail: {versionState.message}
                            </p>
                          )}
                          {versionState?.status === "ready" && (
                            <EmailCreativeFingerprintPanel
                              fingerprintAssetRefs={versionState.data.fingerprint_asset_refs}
                              programId={versionState.data.fingerprint?.program_id}
                              campaignId={versionState.data.fingerprint?.campaign_id}
                              angleId={versionState.data.fingerprint?.angle_id}
                              campaignLookup={campaignLookup}
                              angleLookup={angleLookup}
                              creativeId={creativeId}
                              version={row.version}
                              onNavigateToAsset={onNavigateToAsset}
                            />
                          )}
                          <EmailCreativeVersionBroadcastTable
                            broadcasts={broadcastState?.items}
                            loading={!broadcastState || broadcastState.status === "loading"}
                            error={broadcastState?.status === "error" ? "Failed to load Broadcast usage" : null}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
